import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Card, Icon, Tooltip } from '@node-space/ui-components'
import { LoadingIndicator } from 'components/LoadingIndicator'
import { MERCHANT_RETAINED_EARNINGS_PERCENTAGE } from '../../constants/const'
import { formatWholePercentage } from '../../utils/utils'
import { EarnEmbeddedLabelValueRow } from './EarnEmbeddedLabelValueRow'

export interface EarnEmbeddedCustomerRewardsProgramProps {
  epcTakesEarn: boolean
  canManageDistribution: boolean
  optInRateLabel: string
  onDistributeEarningsClick: () => void
  isStatisticsLoading?: boolean
  customerEarningBalance: string
  customerUnpaidAmount: string
  customerPaidAmount: string
}

export const EarnEmbeddedCustomerRewardsProgram = ({
  epcTakesEarn,
  canManageDistribution,
  optInRateLabel,
  onDistributeEarningsClick,
  isStatisticsLoading,
  customerEarningBalance,
  customerPaidAmount,
  customerUnpaidAmount,
}: EarnEmbeddedCustomerRewardsProgramProps) => {
  const { t } = useTranslation()
  const retainedEarningsPercentage = formatWholePercentage(MERCHANT_RETAINED_EARNINGS_PERCENTAGE)

  return (
    <Card
      variant="static"
      full
      borderRadius={12}
      testId="earn-customer-rewards-program"
      className="bg-surface-interactive p-0"
    >
      <Box flexCol gap={16} className="px-6 py-4">
        <Box flex justify="between" align="center">
          <h4 className="text-h4 text-foreground-prominent">
            {t('earn.customerRewardsProgramTitle')}
          </h4>
          {epcTakesEarn && (
            <Tooltip
              bodyContent={t('earn.customerRewardsProgramTooltip')}
              type="complex"
              position="bottom"
            >
              <Button variant="ghost" noPadding iconElement={<Icon name="Info" />} />
            </Tooltip>
          )}
        </Box>

        <Box flexCol gap={4}>
          <p className="text-label text-foreground-subdued">
            {epcTakesEarn
              ? t('earn.totalBalanceAcrossCustomers')
              : t('earn.totalCustomerFundsEarning')}
          </p>
          {isStatisticsLoading ? (
            <LoadingIndicator testId="earn-total-balance-skeleton" />
          ) : (
            <h1 className="text-h1 text-foreground-prominent">{customerEarningBalance}</h1>
          )}
        </Box>

        {epcTakesEarn ? (
          <Box flexCol gap={16}>
            <Box className="w-full border-t border-stroke-static" />
            <Box flexCol gap={4}>
              <EarnEmbeddedLabelValueRow
                testId="earn-distributed-earnings"
                label={t('earn.distributedEarningsToCustomersLabel')}
                value={customerPaidAmount}
              />
              <EarnEmbeddedLabelValueRow
                testId="earn-opt-in-rate"
                label={t('earn.optInRate')}
                value={optInRateLabel}
                isLoading={isStatisticsLoading}
              />
              <EarnEmbeddedLabelValueRow
                testId="earn-remaining-total"
                label={t('earn.remainingTotal')}
                tooltipContent={t('earn.remainingTotalTooltip')}
                value={customerUnpaidAmount}
              />
            </Box>
          </Box>
        ) : (
          <EarnEmbeddedLabelValueRow
            testId="earn-retained-earnings-percentage"
            label={t('earn.retainedCustomerEarningsPercentage')}
            value={retainedEarningsPercentage}
          />
        )}
      </Box>

      {!epcTakesEarn &&
        (canManageDistribution ? (
          <>
            <Box className="w-full border-t border-stroke-static" />
            <Box flex gap={4} align="center" className="w-full px-6 py-4">
              <p className="text-label flex-1 text-left text-foreground-prominent">
                {t('earn.retainedEarningsDescription', { percentage: retainedEarningsPercentage })}{' '}
                <Button
                  noPadding
                  variant="ghost"
                  className="inline text-label text-foreground-action-secondary"
                  onClick={onDistributeEarningsClick}
                >
                  {t('earn.enableCustomerRewardsDistribution')}
                </Button>
              </p>
              <Box
                className="cursor-pointer text-foreground-action-secondary"
                onClick={onDistributeEarningsClick}
              >
                <Icon name="ArrowRight" size="medium" />
              </Box>
            </Box>
          </>
        ) : (
          <Box
            flex
            gap={4}
            align="center"
            className="w-full border-t border-stroke-static px-6 py-4"
            testId="earn-customer-rewards-program-no-permission"
          >
            <p className="text-label flex-1 text-left text-foreground-prominent pr-2">
              {t('earn.retainedEarningsDescription', { percentage: retainedEarningsPercentage })}{' '}
              {t('earn.workspaceOwnerCanEnableDistribution')}
            </p>
            <Box className="text-foreground-action-secondary">
              <Icon name="ArrowRight" size="medium" />
            </Box>
          </Box>
        ))}
    </Card>
  )
}
